import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Search, Menu, X, Plus, Pencil, Trash2, ChevronRight, ArrowLeft, ShoppingBag, Minus, Package, CheckCircle, Upload, Loader2, Phone, Mail, MapPin, Clock, LogIn, LogOut } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Toaster, toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHIPPING_COST = 3.00;

// Axios config - send cookies
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null); // null=checking, false=not logged in, object=logged in
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setAdmin(r.data))
        .catch(() => { localStorage.removeItem("admin_token"); setAdmin(false); })
        .finally(() => setChecking(false));
    } else {
      setAdmin(false);
      setChecking(false);
    }
  }, []);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("admin_token", r.data.token);
    setAdmin({ email: r.data.email });
    return r.data;
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setAdmin(false);
  };

  // Add auth header to all admin requests
  const authHeaders = () => {
    const token = localStorage.getItem("admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ admin, checking, login, logout, authHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// SEO Meta Component
const SEO = ({ title, description, image, url, type = "website" }) => (
  <Helmet>
    <title>{title ? `${title} | Elixir Lab` : "Elixir Lab - Online Perfume E-Shop"}</title>
    <meta name="description" content={description || "Ανακαλύψτε αρώματα Lattafa, Rasasi, Armaf, Afnan. Αρώματα υψηλής ποιότητας σε προσιτές τιμές."} />
    <meta property="og:title" content={title || "Elixir Lab"} />
    <meta property="og:description" content={description || "Online Perfume E-Shop - Αρώματα υψηλής ποιότητας"} />
    <meta property="og:type" content={type} />
    {image && <meta property="og:image" content={image} />}
    {url && <meta property="og:url" content={url} />}
    <meta property="og:site_name" content="Elixir Lab" />
    <meta name="twitter:card" content="summary_large_image" />
    <link rel="canonical" href={url || "https://elixirlab.gr"} />
  </Helmet>
);

// Helper to resolve image URLs (uploaded images use /api/images/ path)
const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Cart Context
const CartContext = createContext();

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("aroma_cart");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("aroma_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product_id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product_id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { product_id: product.id, name: product.name, brand: product.brand, price: product.price, quantity: 1, image_url: resolveImageUrl(product.image_url) }];
    });
    toast.success(`${product.name} προστέθηκε στο καλάθι`);
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => item.product_id !== productId));
  };

  const updateQuantity = (productId, qty) => {
    if (qty < 1) return removeFromCart(productId);
    setCart((prev) => prev.map((item) => item.product_id === productId ? { ...item, quantity: qty } : item));
  };

  const clearCart = () => setCart([]);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, cartCount, subtotal }}>
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => useContext(CartContext);

// Header
const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { cartCount } = useCart();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header bg-[#0A0A0A]/60 border-b border-[#F4F0EB]/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <svg width="28" height="28" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 4C20 4 12 16 12 24C12 28.4183 15.5817 32 20 32C24.4183 32 28 28.4183 28 24C28 16 20 4 20 4Z" stroke="#C8A97E" strokeWidth="1.5" fill="none"/>
            </svg>
            <span className="font-heading text-2xl md:text-3xl text-[#C8A97E] tracking-tight">ELIXIR LAB</span>
          </Link>
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-[#F4F0EB] hover:text-[#C8A97E] transition-colors text-sm uppercase tracking-wider" data-testid="nav-home">Αρχική</Link>
            <Link to="/shop" className="text-[#F4F0EB] hover:text-[#C8A97E] transition-colors text-sm uppercase tracking-wider" data-testid="nav-shop">Κατάστημα</Link>
            <Link to="/admin" className="text-[#F4F0EB] hover:text-[#C8A97E] transition-colors text-sm uppercase tracking-wider" data-testid="nav-admin">Διαχείριση</Link>
            <Link to="/contact" className="text-[#F4F0EB] hover:text-[#C8A97E] transition-colors text-sm uppercase tracking-wider" data-testid="nav-contact">Επικοινωνία</Link>
          </nav>
          <div className="flex items-center space-x-4">
            <button onClick={() => setSearchOpen(!searchOpen)} className="text-[#F4F0EB] hover:text-[#C8A97E] transition-colors" data-testid="search-toggle"><Search size={20} /></button>
            <Link to="/cart" className="relative text-[#F4F0EB] hover:text-[#C8A97E] transition-colors" data-testid="cart-link">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#C8A97E] text-[#0A0A0A] text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">{cartCount}</span>
              )}
            </Link>
            <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-[#F4F0EB]" data-testid="mobile-menu-toggle">
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {searchOpen && (
          <form onSubmit={handleSearch} className="pb-4">
            <div className="relative">
              <Input type="text" placeholder="Αναζήτηση αρωμάτων..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full bg-[#141414] border-[#F4F0EB]/15 text-[#F4F0EB] placeholder:text-[#7A756C] focus:border-[#C8A97E]" data-testid="search-input" />
              <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A39E93]"><Search size={18} /></button>
            </div>
          </form>
        )}
        {menuOpen && (
          <nav className="md:hidden py-4 border-t border-[#F4F0EB]/10 flex flex-col space-y-4">
            <Link to="/" onClick={() => setMenuOpen(false)} className="text-[#F4F0EB] hover:text-[#C8A97E] text-sm uppercase tracking-wider">Αρχική</Link>
            <Link to="/shop" onClick={() => setMenuOpen(false)} className="text-[#F4F0EB] hover:text-[#C8A97E] text-sm uppercase tracking-wider">Κατάστημα</Link>
            <Link to="/cart" onClick={() => setMenuOpen(false)} className="text-[#F4F0EB] hover:text-[#C8A97E] text-sm uppercase tracking-wider">Καλάθι ({cartCount})</Link>
            <Link to="/admin" onClick={() => setMenuOpen(false)} className="text-[#F4F0EB] hover:text-[#C8A97E] text-sm uppercase tracking-wider">Διαχείριση</Link>
            <Link to="/contact" onClick={() => setMenuOpen(false)} className="text-[#F4F0EB] hover:text-[#C8A97E] text-sm uppercase tracking-wider">Επικοινωνία</Link>
          </nav>
        )}
      </div>
    </header>
  );
};

// Product Card
const ProductCard = ({ product, index }) => {
  const { addToCart } = useCart();
  return (
    <div className="product-card bg-[#141414] border border-[#F4F0EB]/5 overflow-hidden group animate-fade-in-up opacity-0" style={{ animationDelay: `${index * 0.05}s` }} data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.slug || product.id}`}>
        <div className="aspect-square overflow-hidden">
          <img src={resolveImageUrl(product.image_url)} alt={`${product.brand} ${product.name}`} className="product-image w-full h-full object-cover" loading="lazy" />
        </div>
      </Link>
      <div className="p-4">
        <p className="text-[#A39E93] text-xs uppercase tracking-wider mb-1">{product.brand}</p>
        <Link to={`/product/${product.slug || product.id}`}><h3 className="font-heading text-lg text-[#F4F0EB] mb-2 hover:text-[#C8A97E] transition-colors">{product.name}</h3></Link>
        <div className="flex items-center justify-between">
          <span className="text-[#C8A97E] font-medium">€{product.price.toFixed(2)}</span>
          {(product.stock ?? 0) > 0 ? (
            <button onClick={() => addToCart(product)} className="bg-[#C8A97E] text-[#0A0A0A] px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-[#E0C39A] transition-colors" data-testid={`add-to-cart-${product.id}`}>
              <ShoppingBag size={14} className="inline mr-1" />Προσθήκη
            </button>
          ) : (
            <span className="text-red-400 text-xs uppercase tracking-wider font-medium px-3 py-1.5 border border-red-400/30" data-testid={`out-of-stock-${product.id}`}>Εξαντλήθηκε</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Default images for homepage
const DEFAULT_HERO = "https://static.prod-images.emergentagent.com/jobs/50682ffb-78fc-424e-afd7-14262b1ad5ba/images/430c988e1278a9d81e0b7a8b7892a60019a55b3a2e05c4af0a903f92a6739c89.png";
const DEFAULT_CAT_MEN = "https://static.prod-images.emergentagent.com/jobs/50682ffb-78fc-424e-afd7-14262b1ad5ba/images/3084cbba8ff2f1913f7a4b64e71a7a6c466fa2c15674e6c3f3c3077699083029.png";
const DEFAULT_CAT_WOMEN = "https://static.prod-images.emergentagent.com/jobs/50682ffb-78fc-424e-afd7-14262b1ad5ba/images/de0e9383049db22d5bed92de837a3700c6faabb47b81a8fabf9e14a889109cdb.png";
const DEFAULT_CAT_UNISEX = "https://static.prod-images.emergentagent.com/jobs/50682ffb-78fc-424e-afd7-14262b1ad5ba/images/125a362c348b6b311395bb57b8e55ec881b6a4e0d507f4fe4669a06d9ad67e00.png";

// Home
const Home = () => {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsReady, setSettingsReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try { await axios.post(`${API}/seed`); } catch (e) { /* already seeded */ }
      try { const r = await axios.get(`${API}/products`); setProducts(r.data.slice(0, 8)); } catch (e) { console.error("Failed to load products:", e); }
      try { const r = await axios.get(`${API}/settings`); setSettings(r.data); } catch (e) { console.error("Failed to load settings:", e); }
      setSettingsReady(true);
    };
    init();
  }, []);

  const heroImg = settings?.hero_image ? resolveImageUrl(settings.hero_image) : DEFAULT_HERO;
  const catMenImg = settings?.category_men_image ? resolveImageUrl(settings.category_men_image) : DEFAULT_CAT_MEN;
  const catWomenImg = settings?.category_women_image ? resolveImageUrl(settings.category_women_image) : DEFAULT_CAT_WOMEN;
  const catUnisexImg = settings?.category_unisex_image ? resolveImageUrl(settings.category_unisex_image) : DEFAULT_CAT_UNISEX;

  const categories = [
    { id: "men", name: "Ανδρικά", value: "Ανδρικά", image: catMenImg },
    { id: "women", name: "Γυναικεία", value: "Γυναικεία", image: catWomenImg },
    { id: "unisex", name: "Unisex", value: "Unisex", image: catUnisexImg }
  ];

  return (
    <div data-testid="home-page">
      <SEO title="" description="Elixir Lab - Online Perfume E-Shop. Αρώματα Lattafa, Rasasi, Armaf, Afnan σε προσιτές τιμές. Δωρεάν αποστολή." url="https://elixirlab.gr" />
      <section className="relative h-screen">
        <div className="absolute inset-0">
          {!settingsReady ? (
            <div className="w-full h-full bg-[#0A0A0A]" />
          ) : (
            <img src={heroImg} alt="Elixir Lab" className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 hero-gradient" />
        </div>
        <div className="relative h-full flex items-end z-[2]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
            <p className="text-[#C8A97E] text-sm uppercase tracking-[0.3em] mb-4 animate-fade-in-up opacity-0">Online Perfume Shop</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl text-[#F4F0EB] tracking-tighter leading-none mb-6 animate-fade-in-up opacity-0">
              {settings?.hero_title || "ELIXIR LAB"}<br /><span className="text-[#C8A97E]">{settings?.hero_subtitle || "E-Shop"}</span>
            </h1>
            <p className="text-[#A39E93] text-base md:text-lg max-w-md mb-8 animate-fade-in-up opacity-0 animation-delay-200">
              {settings?.hero_text || "Ανακαλύψτε την αραβική πολυτέλεια. Lattafa, Rasasi, Armaf, Afnan και πολλά ακόμα."}
            </p>
            <Button onClick={() => navigate('/shop')} className="btn-primary px-8 py-3 rounded-sm animate-fade-in-up opacity-0 animation-delay-300" data-testid="hero-cta">Αγοράστε Τώρα</Button>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#F4F0EB] tracking-tight">Δημοφιλή Προϊόντα</h2>
            <Link to="/shop" className="text-[#C8A97E] text-sm uppercase tracking-wider hover:text-[#E0C39A] transition-colors" data-testid="view-all-products">Δείτε όλα</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, idx) => <ProductCard key={product.id} product={product} index={idx} />)}
          </div>
        </div>
      </section>
    </div>
  );
};

// Shop
const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API}/products`;
        if (search) url += `?search=${encodeURIComponent(search)}`;
        const r = await axios.get(url);
        setProducts(r.data);
      } catch (e) { toast.error("Σφάλμα φόρτωσης"); }
      setLoading(false);
    };
    fetchProducts();
  }, [search]);

  const cats = [
    { id: "all", name: "Όλα", value: null },
    { id: "men", name: "Ανδρικά", value: "Ανδρικά" },
    { id: "women", name: "Γυναικεία", value: "Γυναικεία" },
    { id: "unisex", name: "Unisex", value: "Unisex" }
  ];

  return (
    <div className="pt-20 min-h-screen" data-testid="shop-page">
      <SEO title="Κατάστημα" description="Περιηγηθείτε σε όλα τα αρώματα. Lattafa, Rasasi, Armaf, Afnan και πολλά ακόμα." url="https://elixirlab.gr/shop" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-heading text-3xl md:text-4xl text-[#F4F0EB] tracking-tight mb-2">
          {search ? `Αποτελέσματα για "${search}"` : "Όλα τα Αρώματα"}
        </h1>
        <p className="text-[#A39E93] mb-8">{products.length} προϊόντα</p>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (<div key={`skeleton-${i}`} className="bg-[#141414] animate-pulse"><div className="aspect-square bg-[#1E1E1E]" /><div className="p-4 space-y-3"><div className="h-3 bg-[#1E1E1E] w-1/3" /><div className="h-5 bg-[#1E1E1E] w-2/3" /><div className="h-4 bg-[#1E1E1E] w-1/4" /></div></div>))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24"><p className="text-[#A39E93] text-lg">Δεν βρέθηκαν προϊόντα</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product, idx) => <ProductCard key={product.id} product={product} index={idx} />)}
          </div>
        )}
      </div>
    </div>
  );
};

// Product Detail
const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { addToCart } = useCart();

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(r => { setProduct(r.data); setLoading(false); }).catch((err) => { console.error("Product load failed:", err); setLoading(false); });
  }, [id]);

  if (loading) return <div className="pt-20 min-h-screen flex items-center justify-center"><div className="text-[#A39E93]">Φόρτωση...</div></div>;
  if (!product) return <div className="pt-20 min-h-screen flex flex-col items-center justify-center"><p className="text-[#A39E93] mb-4">Το προϊόν δεν βρέθηκε</p><Button onClick={() => navigate('/shop')} className="btn-primary">Κατάστημα</Button></div>;

  return (
    <div className="pt-20 min-h-screen" data-testid="product-detail-page">
      <SEO
        title={`${product.brand} ${product.name}`}
        description={product.description}
        image={resolveImageUrl(product.image_url)}
        url={`https://elixirlab.gr/product/${product.slug || product.id}`}
        type="product"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": `${product.brand} ${product.name}`,
          "description": product.description,
          "image": resolveImageUrl(product.image_url),
          "brand": { "@type": "Brand", "name": product.brand },
          "offers": {
            "@type": "Offer",
            "price": product.price.toFixed(2),
            "priceCurrency": "EUR",
            "availability": (product.stock ?? 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "url": `https://elixirlab.gr/product/${product.slug || product.id}`
          }
        })}</script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center text-[#A39E93] hover:text-[#C8A97E] transition-colors mb-8" data-testid="back-button"><ArrowLeft size={18} className="mr-2" />Πίσω</button>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="aspect-square bg-[#141414] overflow-hidden"><img src={resolveImageUrl(product.image_url)} alt={product.name} className="w-full h-full object-cover" /></div>
          <div className="flex flex-col justify-center">
            <p className="text-[#C8A97E] text-sm uppercase tracking-wider mb-2">{product.brand}</p>
            <h1 className="font-heading text-3xl md:text-4xl text-[#F4F0EB] tracking-tight mb-4">{product.name}</h1>
            <p className="text-[#A39E93] mb-6">{product.description}</p>
            <div className="border-t border-b border-[#F4F0EB]/10 py-6 mb-6 space-y-4">
              <div className="flex justify-between"><span className="text-[#7A756C]">Όγκος</span><span className="text-[#F4F0EB]">{product.volume}</span></div>
              {product.notes && <div className="flex justify-between"><span className="text-[#7A756C]">Νότες</span><span className="text-[#F4F0EB] text-right max-w-[60%]">{product.notes}</span></div>}
              <div className="flex justify-between"><span className="text-[#7A756C]">Μεταφορικά</span><span className="text-[#F4F0EB]">€{SHIPPING_COST.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#7A756C]">Διαθεσιμότητα</span>{(product.stock ?? 0) > 0 ? <span className="text-green-400">{product.stock} τεμάχια</span> : <span className="text-red-400 font-medium">Εξαντλήθηκε</span>}</div>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-heading text-3xl text-[#C8A97E]">€{product.price.toFixed(2)}</span>
              {(product.stock ?? 0) > 0 ? (
                <Button onClick={() => addToCart(product)} className="btn-primary px-6 py-3 flex items-center gap-2" data-testid="add-to-cart-btn">
                  <ShoppingBag size={18} />Προσθήκη στο Καλάθι
                </Button>
              ) : (
                <span className="text-red-400 uppercase tracking-wider font-medium px-6 py-3 border border-red-400/30">Εξαντλήθηκε</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cart Page
const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, subtotal } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) return (
    <div className="pt-20 min-h-screen flex flex-col items-center justify-center" data-testid="cart-page">
      <ShoppingBag size={48} className="text-[#7A756C] mb-4" />
      <h2 className="font-heading text-2xl text-[#F4F0EB] mb-2">Το καλάθι είναι άδειο</h2>
      <p className="text-[#A39E93] mb-6">Προσθέστε αρώματα για να ξεκινήσετε</p>
      <Button onClick={() => navigate('/shop')} className="btn-primary">Κατάστημα</Button>
    </div>
  );

  return (
    <div className="pt-20 min-h-screen" data-testid="cart-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-heading text-3xl text-[#F4F0EB] tracking-tight mb-8">Καλάθι Αγορών</h1>
        <div className="space-y-4 mb-8">
          {cart.map((item) => (
            <div key={item.product_id} className="bg-[#141414] border border-[#F4F0EB]/5 p-4 flex items-center gap-4" data-testid={`cart-item-${item.product_id}`}>
              <img src={resolveImageUrl(item.image_url)} alt={item.name} className="w-16 h-16 object-cover" />
              <div className="flex-1 min-w-0">
                <p className="text-[#A39E93] text-xs">{item.brand}</p>
                <p className="text-[#F4F0EB] font-medium truncate">{item.name}</p>
                <p className="text-[#C8A97E]">€{item.price.toFixed(2)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center bg-[#1E1E1E] text-[#F4F0EB] hover:bg-[#C8A97E] hover:text-[#0A0A0A] transition-colors" data-testid={`qty-minus-${item.product_id}`}><Minus size={14} /></button>
                <span className="w-8 text-center text-[#F4F0EB]">{item.quantity}</span>
                <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center bg-[#1E1E1E] text-[#F4F0EB] hover:bg-[#C8A97E] hover:text-[#0A0A0A] transition-colors" data-testid={`qty-plus-${item.product_id}`}><Plus size={14} /></button>
              </div>
              <p className="text-[#F4F0EB] font-medium w-20 text-right">€{(item.price * item.quantity).toFixed(2)}</p>
              <button onClick={() => removeFromCart(item.product_id)} className="text-[#7A756C] hover:text-red-500 transition-colors" data-testid={`remove-${item.product_id}`}><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
        <div className="bg-[#141414] border border-[#F4F0EB]/5 p-6 space-y-3">
          <div className="flex justify-between text-[#A39E93]"><span>Υποσύνολο</span><span>€{subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between text-[#A39E93]"><span>Μεταφορικά</span><span>€{SHIPPING_COST.toFixed(2)}</span></div>
          <div className="border-t border-[#F4F0EB]/10 pt-3 flex justify-between text-[#F4F0EB] font-medium text-lg"><span>Σύνολο</span><span className="text-[#C8A97E]">€{(subtotal + SHIPPING_COST).toFixed(2)}</span></div>
          <Button onClick={() => navigate('/checkout')} className="btn-primary w-full py-3 mt-4" data-testid="checkout-btn">Ολοκλήρωση Παραγγελίας</Button>
        </div>
      </div>
    </div>
  );
};

// Checkout Page
const Checkout = () => {
  const { cart, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', customer_address: '', customer_city: '', customer_zip: '', payment_method: 'cod', notes: '' });

  if (cart.length === 0 && !orderSuccess) { navigate('/cart'); return null; }

  if (orderSuccess) return (
    <div className="pt-20 min-h-screen flex flex-col items-center justify-center px-6" data-testid="order-success">
      <CheckCircle size={64} className="text-green-500 mb-4" />
      <h2 className="font-heading text-3xl text-[#F4F0EB] mb-2">Η παραγγελία σας καταχωρήθηκε!</h2>
      <p className="text-[#A39E93] mb-2">Αριθμός παραγγελίας: <span className="text-[#C8A97E] font-bold">{orderSuccess.order_number}</span></p>
      <p className="text-[#A39E93] mb-1">Σύνολο: <span className="text-[#F4F0EB]">€{orderSuccess.total.toFixed(2)}</span></p>
      <p className="text-[#A39E93] mb-6">
        {orderSuccess.payment_method === 'cod' && 'Πληρωμή με αντικαταβολή κατά την παράδοση.'}
        {orderSuccess.payment_method === 'paypal' && 'Η πληρωμή μέσω PayPal ολοκληρώθηκε επιτυχώς!'}
        {orderSuccess.payment_method === 'bank_transfer' && 'Θα λάβετε email με τα στοιχεία τραπεζικής μεταφοράς.'}
      </p>
      {orderSuccess.payment_method === 'bank_transfer' && (
        <div className="bg-[#141414] border border-[#F4F0EB]/10 p-4 mb-6 text-center">
          <p className="text-[#C8A97E] font-medium mb-2">Στοιχεία Τραπεζικής Μεταφοράς</p>
          <p className="text-[#A39E93] text-sm">IBAN: GR00 0000 0000 0000 0000 0000 000</p>
          <p className="text-[#A39E93] text-sm">Δικαιούχος: Elixir Lab</p>
          <p className="text-[#A39E93] text-sm">Αιτιολογία: {orderSuccess.order_number}</p>
        </div>
      )}
      <Button onClick={() => navigate('/shop')} className="btn-primary">Συνέχεια Αγορών</Button>
    </div>
  );

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!form.customer_name || !form.customer_phone || !form.customer_address || !form.customer_city || !form.customer_zip) {
      toast.error("Συμπληρώστε όλα τα υποχρεωτικά πεδία"); return false;
    }
    if (form.payment_method === 'paypal') {
      toast.error("Χρησιμοποιήστε το κουμπί PayPal για πληρωμή"); return false;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/orders`, { ...form, items: cart });
      setOrderSuccess(r.data);
      clearCart();
      toast.success("Η παραγγελία σας καταχωρήθηκε!");
      return true;
    } catch (e) { toast.error("Σφάλμα κατά την υποβολή"); return false; }
    finally { setSubmitting(false); }
  };

  const validateForm = () => {
    if (!form.customer_name || !form.customer_phone || !form.customer_address || !form.customer_city || !form.customer_zip) {
      toast.error("Συμπληρώστε όλα τα υποχρεωτικά πεδία");
      return false;
    }
    return true;
  };

  const handlePayPalSuccess = async (details) => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/orders`, {
        ...form,
        payment_method: 'paypal',
        items: cart,
        notes: form.notes ? `${form.notes} | PayPal: ${details.id}` : `PayPal: ${details.id}`
      });
      setOrderSuccess(r.data);
      clearCart();
      toast.success("Η πληρωμή PayPal ολοκληρώθηκε!");
    } catch (e) { toast.error("Σφάλμα κατά την καταχώρηση"); }
    setSubmitting(false);
  };

  const orderTotal = (subtotal + SHIPPING_COST).toFixed(2);

  const u = (field, val) => setForm({ ...form, [field]: val });

  return (
    <div className="pt-20 min-h-screen" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-heading text-3xl text-[#F4F0EB] tracking-tight mb-8">Ολοκλήρωση Παραγγελίας</h1>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Info */}
            <div className="bg-[#141414] border border-[#F4F0EB]/5 p-6">
              <h3 className="text-[#F4F0EB] font-medium mb-4 text-lg">Στοιχεία Αποστολής</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="text-[#A39E93]">Ονοματεπώνυμο *</Label><Input value={form.customer_name} onChange={e => u('customer_name', e.target.value)} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB] mt-1" required data-testid="input-name" /></div>
                <div><Label className="text-[#A39E93]">Email</Label><Input type="email" value={form.customer_email} onChange={e => u('customer_email', e.target.value)} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB] mt-1" data-testid="input-email" /></div>
                <div><Label className="text-[#A39E93]">Τηλέφωνο *</Label><Input value={form.customer_phone} onChange={e => u('customer_phone', e.target.value)} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB] mt-1" required data-testid="input-phone" /></div>
                <div><Label className="text-[#A39E93]">Πόλη *</Label><Input value={form.customer_city} onChange={e => u('customer_city', e.target.value)} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB] mt-1" required data-testid="input-city" /></div>
                <div className="sm:col-span-2"><Label className="text-[#A39E93]">Διεύθυνση *</Label><Input value={form.customer_address} onChange={e => u('customer_address', e.target.value)} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB] mt-1" required data-testid="input-address" /></div>
                <div><Label className="text-[#A39E93]">Τ.Κ. *</Label><Input value={form.customer_zip} onChange={e => u('customer_zip', e.target.value)} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB] mt-1" required data-testid="input-zip" /></div>
                <div><Label className="text-[#A39E93]">Σημειώσεις</Label><Input value={form.notes} onChange={e => u('notes', e.target.value)} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB] mt-1" placeholder="Π.χ. όροφος, κουδούνι" data-testid="input-notes" /></div>
              </div>
            </div>
            {/* Payment */}
            <div className="bg-[#141414] border border-[#F4F0EB]/5 p-6">
              <h3 className="text-[#F4F0EB] font-medium mb-4 text-lg">Τρόπος Πληρωμής</h3>
              <div className="space-y-3">
                {[
                  { val: 'cod', label: 'Αντικαταβολή', desc: 'Πληρωμή κατά την παράδοση (+€2)' },
                  { val: 'paypal', label: 'PayPal', desc: 'Online πληρωμή μέσω PayPal' },
                  { val: 'bank_transfer', label: 'Τραπεζική Μεταφορά', desc: 'Κατάθεση σε τραπεζικό λογαριασμό' },
                ].map(pm => (
                  <label key={pm.val} className={`flex items-center gap-4 p-4 cursor-pointer border transition-all ${form.payment_method === pm.val ? 'border-[#C8A97E] bg-[#C8A97E]/5' : 'border-[#F4F0EB]/10 hover:border-[#F4F0EB]/20'}`} data-testid={`payment-${pm.val}`}>
                    <input type="radio" name="payment" value={pm.val} checked={form.payment_method === pm.val} onChange={() => u('payment_method', pm.val)} className="accent-[#C8A97E]" />
                    <div><p className="text-[#F4F0EB] font-medium">{pm.label}</p><p className="text-[#7A756C] text-sm">{pm.desc}</p></div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {/* Order Summary */}
          <div className="bg-[#141414] border border-[#F4F0EB]/5 p-6 h-fit lg:sticky lg:top-24">
            <h3 className="text-[#F4F0EB] font-medium mb-4 text-lg">Σύνοψη Παραγγελίας</h3>
            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
              {cart.map(item => (
                <div key={item.product_id} className="flex items-center gap-3">
                  <img src={resolveImageUrl(item.image_url)} alt={item.name} className="w-10 h-10 object-cover" />
                  <div className="flex-1 min-w-0"><p className="text-[#F4F0EB] text-sm truncate">{item.name}</p><p className="text-[#7A756C] text-xs">x{item.quantity}</p></div>
                  <p className="text-[#A39E93] text-sm">€{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-[#F4F0EB]/10 pt-3 space-y-2">
              <div className="flex justify-between text-[#A39E93] text-sm"><span>Υποσύνολο</span><span>€{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-[#A39E93] text-sm"><span>Μεταφορικά</span><span>€{SHIPPING_COST.toFixed(2)}</span></div>
              {form.payment_method === 'cod' && <div className="flex justify-between text-[#A39E93] text-sm"><span>Αντικαταβολή</span><span>€2.00</span></div>}
              <div className="border-t border-[#F4F0EB]/10 pt-2 flex justify-between text-[#F4F0EB] font-medium">
                <span>Σύνολο</span>
                <span className="text-[#C8A97E] text-lg">€{(subtotal + SHIPPING_COST + (form.payment_method === 'cod' ? 2 : 0)).toFixed(2)}</span>
              </div>
            </div>
            {form.payment_method === 'paypal' ? (
              <div className="mt-6" data-testid="paypal-buttons-container">
                <PayPalScriptProvider options={{ clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID, currency: "EUR" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 45 }}
                    disabled={submitting}
                    onClick={(data, actions) => {
                      if (!validateForm()) return actions.reject();
                      return actions.resolve();
                    }}
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        purchase_units: [{
                          amount: { value: orderTotal, currency_code: "EUR" },
                          description: `Elixir Lab - ${cart.length} προϊόντα`
                        }]
                      });
                    }}
                    onApprove={async (data, actions) => {
                      const details = await actions.order.capture();
                      await handlePayPalSuccess(details);
                    }}
                    onError={(err) => {
                      console.error("PayPal error:", err);
                      toast.error("Σφάλμα PayPal. Δοκιμάστε ξανά.");
                    }}
                  />
                </PayPalScriptProvider>
              </div>
            ) : (
              <Button type="submit" className="btn-primary w-full py-3 mt-6" disabled={submitting} data-testid="place-order-btn">
                {submitting ? 'Υποβολή...' : 'Υποβολή Παραγγελίας'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// Settings Image Uploader
const SettingsUploader = ({ label, onUpload, uploading, setUploading }) => {
  const { authHeaders } = useAuth();
  return (
  <label className="block cursor-pointer">
    <div className="flex items-center justify-center gap-2 bg-[#1E1E1E] border border-[#F4F0EB]/15 text-[#A39E93] hover:text-[#C8A97E] hover:border-[#C8A97E] transition-all py-2 px-4 text-sm">
      <Upload size={16} />{uploading ? 'Ανέβασμα...' : label}
    </div>
    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      try {
        const r = await axios.post(`${API}/upload`, fd, { headers: authHeaders() });
        onUpload(r.data.url);
        toast.success('Η εικόνα ανέβηκε!');
      } catch (err) { toast.error('Σφάλμα upload'); }
      setUploading(false);
    }} />
  </label>
  );
};

// Admin
// Admin Login Page
const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα σύνδεσης');
    }
    setLoading(false);
  };

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center" data-testid="admin-login-page">
      <div className="bg-[#141414] border border-[#F4F0EB]/5 p-8 w-full max-w-md">
        <h2 className="font-heading text-2xl text-[#F4F0EB] text-center mb-6">Σύνδεση Διαχειριστή</h2>
        {error && <p className="text-red-400 text-sm text-center mb-4" data-testid="login-error">{error}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#A39E93]">Email</Label>
            <Input value={email} onChange={e => setEmail(e.target.value)} type="email" className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" required data-testid="admin-email-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#A39E93]">Κωδικός</Label>
            <Input value={password} onChange={e => setPassword(e.target.value)} type="password" className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" required data-testid="admin-password-input" />
          </div>
          <Button type="submit" className="btn-primary w-full py-3" disabled={loading} data-testid="admin-login-btn">
            {loading ? 'Σύνδεση...' : 'Σύνδεση'}
          </Button>
        </form>
      </div>
    </div>
  );
};

// Protected Admin Wrapper
const AdminProtected = () => {
  const { admin, checking } = useAuth();
  if (checking) return <div className="pt-20 min-h-screen flex items-center justify-center"><p className="text-[#A39E93]">Φόρτωση...</p></div>;
  if (!admin) return <AdminLogin />;
  return <Admin />;
};

const Admin = () => {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ name: '', brand: '', description: '', price: '', category: 'Ανδρικά', image_url: '', notes: '', volume: '100ml', stock: '0' });
  const [siteSettings, setSiteSettings] = useState(null);
  const [settingsUploading, setSettingsUploading] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const { authHeaders, logout } = useAuth();
  const navigate = useNavigate();
  const hdrs = () => ({ headers: authHeaders() });

  const fetchProducts = async () => { try { const r = await axios.get(`${API}/products`); setProducts(r.data); } catch (e) { console.error(e); } setLoading(false); };
  const fetchOrders = async () => { try { const r = await axios.get(`${API}/orders`, hdrs()); setOrders(r.data); } catch (e) { console.error(e); } };
  const fetchSettings = async () => {
    try {
      const r = await axios.get(`${API}/settings`);
      setSiteSettings(r.data);
    } catch (e) {
      setSiteSettings({ hero_image: '', hero_title: 'ELIXIR LAB', hero_subtitle: 'E-Shop', hero_text: '', category_men_image: '', category_women_image: '', category_unisex_image: '' });
    }
    setSettingsLoaded(true);
  };

  useEffect(() => { fetchProducts(); fetchOrders(); fetchSettings(); }, []);

  const handleOpenDialog = (product = null) => {
    if (product) { setEditingProduct(product); setFormData({ name: product.name, brand: product.brand, description: product.description, price: product.price.toString(), category: product.category, image_url: product.image_url, notes: product.notes || '', volume: product.volume || '100ml', stock: (product.stock ?? 0).toString() }); }
    else { setEditingProduct(null); setFormData({ name: '', brand: '', description: '', price: '', category: 'Ανδρικά', image_url: '', notes: '', volume: '100ml', stock: '0' }); }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { ...formData, price: parseFloat(formData.price), stock: parseInt(formData.stock) || 0 };
    try {
      if (editingProduct) { await axios.put(`${API}/products/${editingProduct.id}`, data, hdrs()); toast.success("Ενημερώθηκε!"); }
      else { await axios.post(`${API}/products`, data, hdrs()); toast.success("Προστέθηκε!"); }
      setDialogOpen(false); fetchProducts();
    } catch (e) { toast.error("Σφάλμα"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Διαγραφή προϊόντος;")) return;
    try { await axios.delete(`${API}/products/${id}`, hdrs()); toast.success("Διαγράφηκε!"); fetchProducts(); } catch (e) { toast.error("Σφάλμα"); }
  };

  const updateOrderStatus = async (orderId, status) => {
    try { await axios.put(`${API}/orders/${orderId}/status?status=${encodeURIComponent(status)}`, {}, hdrs()); toast.success("Ενημερώθηκε!"); fetchOrders(); } catch (e) { toast.error("Σφάλμα"); }
  };

  const statusColors = { "Νέα": "bg-blue-500/20 text-blue-400", "Σε εξέλιξη": "bg-yellow-500/20 text-yellow-400", "Ολοκληρωμένη": "bg-green-500/20 text-green-400", "Ακυρωμένη": "bg-red-500/20 text-red-400" };
  const paymentLabels = { cod: "Αντικαταβολή", paypal: "PayPal", bank_transfer: "Τραπεζική" };

  return (
    <div className="pt-20 min-h-screen" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-3xl md:text-4xl text-[#F4F0EB] tracking-tight">Διαχείριση</h1>
          <button onClick={() => { logout(); navigate('/'); }} className="flex items-center gap-2 text-[#A39E93] hover:text-red-400 transition-colors text-sm" data-testid="admin-logout-btn">
            <LogOut size={18} />Αποσύνδεση
          </button>
        </div>
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="bg-[#141414] border border-[#F4F0EB]/10 mb-6">
            <TabsTrigger value="orders" data-testid="tab-orders">Παραγγελίες ({orders.length})</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Προϊόντα ({products.length})</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Αρχική Σελίδα</TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            {orders.length === 0 ? <p className="text-[#A39E93] text-center py-12">Δεν υπάρχουν παραγγελίες</p> : (
              <div className="space-y-4">
                {orders.map(order => (
                  <div key={order.id} className="bg-[#141414] border border-[#F4F0EB]/5 p-4 md:p-6" data-testid={`order-${order.id}`}>
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <span className="text-[#C8A97E] font-bold">{order.order_number}</span>
                      <Badge className={statusColors[order.status] || ""}>{order.status}</Badge>
                      <span className="text-[#7A756C] text-sm">{new Date(order.created_at).toLocaleDateString('el-GR')}</span>
                      <span className="text-[#A39E93] text-sm">{paymentLabels[order.payment_method]}</span>
                      <span className="text-[#F4F0EB] font-medium ml-auto">€{order.total.toFixed(2)}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-[#F4F0EB]">{order.customer_name}</p>
                        <p className="text-[#A39E93] text-sm">{order.customer_phone} | {order.customer_email}</p>
                        <p className="text-[#7A756C] text-sm">{order.customer_address}, {order.customer_city} {order.customer_zip}</p>
                        {order.notes && <p className="text-[#7A756C] text-sm mt-1">Σημ: {order.notes}</p>}
                      </div>
                      <div className="space-y-1">
                        {order.items.map((item) => <p key={item.product_id} className="text-[#A39E93] text-sm">{item.quantity}x {item.brand} {item.name} - €{(item.price * item.quantity).toFixed(2)}</p>)}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {["Νέα", "Σε εξέλιξη", "Ολοκληρωμένη", "Ακυρωμένη"].map(s => (
                        <button key={s} onClick={() => updateOrderStatus(order.id, s)}
                          className={`px-3 py-1 text-xs transition-all ${order.status === s ? 'bg-[#C8A97E] text-[#0A0A0A]' : 'bg-[#1E1E1E] text-[#A39E93] hover:text-[#F4F0EB]'}`}
                          data-testid={`order-status-${order.id}-${s}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="products">
            <div className="flex justify-end mb-4">
              <Button onClick={() => handleOpenDialog()} className="btn-primary flex items-center gap-2" data-testid="add-product-btn"><Plus size={18} />Προσθήκη</Button>
            </div>
            <div className="bg-[#141414] border border-[#F4F0EB]/10 overflow-x-auto">
              <table className="w-full admin-table">
                <thead><tr><th className="text-left p-4">Εικόνα</th><th className="text-left p-4">Όνομα</th><th className="text-left p-4">Brand</th><th className="text-left p-4">Τιμή</th><th className="text-left p-4">Απόθεμα</th><th className="text-right p-4">Ενέργειες</th></tr></thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p.id} data-testid={`admin-product-row-${p.id}`}>
                      <td className="p-4"><img src={resolveImageUrl(p.image_url)} alt={p.name} className="w-12 h-12 object-cover" /></td>
                      <td className="p-4 text-[#F4F0EB]">{p.name}</td>
                      <td className="p-4 text-[#A39E93]">{p.brand}</td>
                      <td className="p-4 text-[#C8A97E]">€{p.price.toFixed(2)}</td>
                      <td className="p-4">{(p.stock ?? 0) > 0 ? <span className="text-green-400">{p.stock}</span> : <span className="text-red-400 font-medium">Εξαντλήθηκε</span>}</td>
                      <td className="p-4"><div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleOpenDialog(p)} className="p-2 text-[#A39E93] hover:text-[#C8A97E]"><Pencil size={18} /></button>
                        <button onClick={() => handleDelete(p.id)} className="p-2 text-[#A39E93] hover:text-red-500"><Trash2 size={18} /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogContent className="bg-[#141414] border-[#F4F0EB]/10 text-[#F4F0EB] max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{editingProduct ? 'Επεξεργασία' : 'Νέο Προϊόν'}</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2"><Label>Όνομα</Label><Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" required data-testid="product-name-input" /></div>
                  <div className="space-y-2"><Label>Brand</Label><Input value={formData.brand} onChange={e => setFormData({...formData, brand: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" required data-testid="product-brand-input" /></div>
                  <div className="space-y-2"><Label>Περιγραφή</Label><Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB] min-h-[80px]" required data-testid="product-description-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Τιμή (€)</Label><Input type="number" step="0.01" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" required data-testid="product-price-input" /></div>
                    <div className="space-y-2"><Label>Όγκος</Label><Input value={formData.volume} onChange={e => setFormData({...formData, volume: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" data-testid="product-volume-input" /></div>
                  </div>
                  <div className="space-y-2"><Label>Διαθέσιμα Τεμάχια</Label><Input type="number" min="0" value={formData.stock} onChange={e => setFormData({...formData, stock: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" data-testid="product-stock-input" /></div>
                  <div className="space-y-2"><Label>Εικόνα Προϊόντος</Label>
                    {formData.image_url && (
                      <div className="w-full h-32 bg-[#0A0A0A] border border-[#F4F0EB]/10 overflow-hidden mb-2">
                        <img src={formData.image_url.startsWith('/api/') ? `${BACKEND_URL}${formData.image_url}` : formData.image_url} alt="Preview" className="w-full h-full object-contain" />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <label className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-center gap-2 bg-[#1E1E1E] border border-[#F4F0EB]/15 text-[#A39E93] hover:text-[#C8A97E] hover:border-[#C8A97E] transition-all py-2 px-4 text-sm">
                          <Upload size={16} />{uploading ? 'Ανέβασμα...' : 'Ανέβασμα Εικόνας'}
                        </div>
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploading(true);
                          const fd = new FormData();
                          fd.append('file', file);
                          try {
                            const r = await axios.post(`${API}/upload`, fd, hdrs());
                            setFormData(prev => ({...prev, image_url: r.data.url}));
                            toast.success('Η εικόνα ανέβηκε!');
                          } catch (err) { toast.error('Σφάλμα upload'); }
                          setUploading(false);
                        }} data-testid="product-image-upload" />
                      </label>
                    </div>
                    <p className="text-[#7A756C] text-xs">ή βάλε URL:</p>
                    <Input value={formData.image_url} onChange={e => setFormData({...formData, image_url: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" placeholder="https://..." data-testid="product-image-input" />
                  </div>
                  <div className="space-y-2"><Label>Νότες</Label><Input value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" placeholder="π.χ. Βανίλια, Σανταλόξυλο" data-testid="product-notes-input" /></div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#F4F0EB]/15 text-[#A39E93]">Ακύρωση</Button>
                    <Button type="submit" className="btn-primary" data-testid="save-product-btn">{editingProduct ? 'Αποθήκευση' : 'Προσθήκη'}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="settings">
            {!settingsLoaded || !siteSettings ? (
              <div className="text-center py-12 text-[#A39E93]">Φόρτωση ρυθμίσεων...</div>
            ) : (
            <div className="bg-[#141414] border border-[#F4F0EB]/5 p-6 md:p-8 space-y-8">
              <h3 className="text-[#F4F0EB] font-heading text-2xl mb-6">Ρυθμίσεις Αρχικής Σελίδας</h3>

              {/* Hero Section */}
              <div className="space-y-4 border-b border-[#F4F0EB]/10 pb-8">
                <h4 className="text-[#C8A97E] font-medium uppercase tracking-wider text-sm">Hero Section</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A39E93]">Τίτλος</Label>
                    <Input value={siteSettings.hero_title || ''} onChange={e => setSiteSettings({...siteSettings, hero_title: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" data-testid="settings-hero-title" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A39E93]">Υπότιτλος</Label>
                    <Input value={siteSettings.hero_subtitle || ''} onChange={e => setSiteSettings({...siteSettings, hero_subtitle: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" data-testid="settings-hero-subtitle" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A39E93]">Κείμενο</Label>
                  <Textarea value={siteSettings.hero_text || ''} onChange={e => setSiteSettings({...siteSettings, hero_text: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB] min-h-[60px]" data-testid="settings-hero-text" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A39E93]">Φωτογραφία Hero</Label>
                  {(siteSettings.hero_image || DEFAULT_HERO) && (
                    <div className="w-full h-40 bg-[#0A0A0A] border border-[#F4F0EB]/10 overflow-hidden mb-2">
                      <img src={siteSettings.hero_image ? resolveImageUrl(siteSettings.hero_image) : DEFAULT_HERO} alt="Hero" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <SettingsUploader label="Αλλαγή Hero" onUpload={(url) => setSiteSettings({...siteSettings, hero_image: url})} uploading={settingsUploading} setUploading={setSettingsUploading} />
                </div>
              </div>

              {/* Category Images */}
              <div className="space-y-4">
                <h4 className="text-[#C8A97E] font-medium uppercase tracking-wider text-sm">Φωτογραφίες Κατηγοριών</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { key: 'category_men_image', label: 'Ανδρικά', fallback: DEFAULT_CAT_MEN },
                    { key: 'category_women_image', label: 'Γυναικεία', fallback: DEFAULT_CAT_WOMEN },
                    { key: 'category_unisex_image', label: 'Unisex', fallback: DEFAULT_CAT_UNISEX }
                  ].map(cat => (
                    <div key={cat.key} className="space-y-2">
                      <Label className="text-[#A39E93]">{cat.label}</Label>
                      <div className="aspect-[3/4] bg-[#0A0A0A] border border-[#F4F0EB]/10 overflow-hidden mb-2">
                        <img src={siteSettings[cat.key] ? resolveImageUrl(siteSettings[cat.key]) : cat.fallback} alt={cat.label} className="w-full h-full object-cover" />
                      </div>
                      <SettingsUploader label={`Αλλαγή ${cat.label}`} onUpload={(url) => setSiteSettings({...siteSettings, [cat.key]: url})} uploading={settingsUploading} setUploading={setSettingsUploading} />
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={async () => {
                try {
                  await axios.put(`${API}/settings`, siteSettings, hdrs());
                  toast.success("Οι ρυθμίσεις αποθηκεύτηκαν!");
                } catch (e) { toast.error("Σφάλμα αποθήκευσης"); }
              }} className="btn-primary px-8 py-3" data-testid="save-settings-btn">
                Αποθήκευση Ρυθμίσεων
              </Button>
            </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};


// Terms Page
const Terms = () => (
  <div className="pt-20 min-h-screen" data-testid="terms-page">
    <SEO title="Όροι Χρήσης" description="Όροι χρήσης του ηλεκτρονικού καταστήματος Elixir Lab." url="https://elixirlab.gr/terms" />
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
      <h1 className="font-heading text-3xl md:text-4xl text-[#F4F0EB] tracking-tight mb-8">Όροι Χρήσης</h1>
      <div className="bg-[#141414] border border-[#F4F0EB]/5 p-6 md:p-10 space-y-6 text-[#A39E93] text-sm leading-relaxed">
        <p>Το elixirlab.gr είναι το ηλεκτρονικό κατάστημα αρωμάτων της εταιρείας Elixir Lab. Πριν εισέλθετε στο ηλεκτρονικό κατάστημα και περιηγηθείτε στην ιστοσελίδα μας, σας καλούμε να συμβουλευτείτε τους κάτωθι όρους και προϋποθέσεις, που εφαρμόζονται ειδικά για τη χρήση του ηλεκτρονικού καταστήματος της εταιρίας, που βρίσκεται στην διεύθυνση elixirlab.gr. Βεβαιωθείτε ότι συμφωνείτε με τους κάτωθι όρους και προϋποθέσεις διότι η περαιτέρω χρήση και η περιήγηση σας στην άνω ιστοσελίδα elixirlab.gr συνεπάγεται την ρητή και ανεπιφύλακτη συγκατάθεση και συναίνεση σας με αυτούς.</p>

        <h2 className="text-[#C8A97E] font-medium text-base mt-8">Γενικοί Όροι</h2>
        <p>Το elixirlab.gr διατηρεί το δικαίωμα να τροποποιεί μονομερώς ή να ανανεώνει τους παρόντες όρους και τις προϋποθέσεις των συναλλαγών, που γίνονται μέσω του ηλεκτρονικού της καταστήματος, σύμφωνα με τις ανάγκες της και τα συναλλακτικά ήθη. Το elixirlab.gr αναλαμβάνει την υποχρέωση να ενημερώνει τους χρηστές για τυχόν τροποποιήσεις καθώς και για οποιαδήποτε αλλαγή, μέσα από την ιστοσελίδα του παρόντος ηλεκτρονικού καταστήματος. Διευκρινίζεται ότι η αλλαγή όρων δεν καταλαμβάνει παραγγελίες τις οποίες έχετε ήδη κάνει.</p>

        <h2 className="text-[#C8A97E] font-medium text-base mt-8">Παρεχόμενες Πληροφορίες & Προϊόντα</h2>
        <p>Το elixirlab.gr δεσμεύεται ως προς την πληρότητα και την εγκυρότητα των πληροφοριών που παρατίθενται στην ιστοσελίδα της elixirlab.gr, τόσο όσον αφορά την ύπαρξη των ουσιωδών χαρακτηριστικών που κατά περίπτωση περιγράφονται για κάθε προϊόν που διαθέτει, όσο και για την ακρίβεια των στοιχείων που αφορούν τις παρεχόμενες, από το ηλεκτρονικό κατάστημα elixirlab.gr, υπηρεσιών, υπό την επιφύλαξη τυχόν τεχνικών ή τυπογραφικών λαθών, που έχουν διαφύγει της προσοχής ή έχουν προκύψει ακούσια ή λόγω τυχόν διακοπών λειτουργίας της ιστοσελίδας εκ λόγων ανωτέρας βίας.</p>
        <p>Οι φωτογραφίες που απεικονίζουν τα προϊόντα είναι ενδεικτικές και δεν μπορούν σε καμία περίπτωση να λειτουργήσουν δεσμευτικά για το elixirlab.gr. Επίσης, σε ορισμένες περιπτώσεις ενδέχεται να μην απεικονίζουν πιστά το τελικό προϊόν.</p>

        <h2 className="text-[#C8A97E] font-medium text-base mt-8">Περιορισμός Ευθύνης</h2>
        <p>Το elixirlab.gr ουδεμία εγγύηση μπορεί να παράσχει για τη διαθεσιμότητα των προϊόντων, αλλά εγγυάται την έγκαιρη ενημέρωση των τελικών καταναλωτών περί της μη διαθεσιμότητάς τους.</p>
        <p>Το elixirlab.gr ευθύνεται μόνο για δόλο και βαριά αμέλεια, σε περίπτωση καθυστέρησης στην παράδοση παραγγελθέντων προϊόντων και για πληροφορίες που η ίδια δίνει ή υπηρεσίες που παρέχει μέσω του δικτυακού τόπου της.</p>
        <p>Το ηλεκτρονικό κατάστημα elixirlab.gr δεν φέρει ευθύνη για τυχόν τεχνικά προβλήματα που τυχόν παρουσιαστούν στους χρήστες όταν επιχειρούν την πρόσβαση στον δικτυακό τόπο και κατά την διάρκεια αυτής και έχουν σχέση με τη λειτουργία ή συμβατότητα της δικής τους υποδομής με τη χρήση του δικτυακού τόπου. Επίσης, το elixirlab.gr ουδεμία ευθύνη έχει για πράξεις ή παραλείψεις τρίτων και ιδιαίτερα μη επιτρεπόμενες παρεμβάσεις τρίτων σε προϊόντα ή/και υπηρεσίες ή/και πληροφορίες που διατίθενται μέσω αυτού.</p>

        <h2 className="text-[#C8A97E] font-medium text-base mt-8">Δικαιώματα Πνευματικής Ιδιοκτησίας</h2>
        <p>Όλο το περιεχόμενο των ιστοσελίδων, που αναρτάται στο elixirlab.gr, συμπεριλαμβανομένων εικόνων, γραφικών, φωτογραφιών, σχεδίων, κειμένων, παρεχόμενων υπηρεσιών και προϊόντων αποτελούν πνευματική ιδιοκτησία του elixirlab.gr και προστατεύονται κατά τις σχετικές διατάξεις του ελληνικού δικαίου, του ευρωπαϊκού δικαίου και των διεθνών συμβάσεων.</p>
        <p>Απαγορεύεται οποιαδήποτε αντιγραφή, αναλογική / ψηφιακή εγγραφή και μηχανική αναπαραγωγή, διανομή, μεταφορά, downloading, μεταποίηση, μεταπώληση, δημιουργία παράγωγης εργασίας ή παραπλάνηση του κοινού σχετικά με τον πραγματικό παροχέα του περιεχομένου των διαδικτυακών τόπων. Τυχόν αναπαραγωγή, επανέκδοση, μεταφόρτωση, ανακοίνωση, διάδοση ή μετάδοση ή οποιαδήποτε άλλη χρήση του περιεχομένου με οποιοδήποτε τρόπο ή μέσο για εμπορικούς ή άλλους σκοπούς επιτρέπεται μόνο κατόπιν προηγούμενης γραπτής άδειας του elixirlab.gr ή οιουδήποτε άλλου νόμιμου δικαιούχου των ανωτέρω πνευματικών δικαιωμάτων.</p>
        <p>Τα ονόματα, εικόνες, λογότυπα και διακριτικά γνωρίσματα που αντιπροσωπεύουν το elixirlab.gr ή/και τρίτα μέρη συμβεβλημένα με αυτούς καθώς και τα προϊόντα ή τις υπηρεσίες τους, είναι αποκλειστικά σήματα και διακριτικά γνωρίσματα του elixirlab.gr ή/και των άνω τρίτων μερών και προστατεύονται από τους ελληνικούς, κοινοτικούς και διεθνείς νόμους περί εμπορικών σημάτων και βιομηχανικής και πνευματικής ιδιοκτησίας και αθεμίτου ανταγωνισμού.</p>
        <p>Σε κάθε περίπτωση η εμφάνιση και έκθεσή τους στις ιστοσελίδες elixirlab.gr δεν θα πρέπει κατά κανένα τρόπο να εκληφθεί ως μεταβίβαση ή εκχώρηση άδειας ή δικαιώματος χρήσης τους.</p>

        <h2 className="text-[#C8A97E] font-medium text-base mt-8">Υποχρεώσεις Χρήστη</h2>
        <p>Οι χρήστες των ιστοσελίδων elixirlab.gr αποδέχονται ότι δεν θα χρησιμοποιούν αυτές για αποστολή, δημοσίευση, αποστολή με e-mail ή μετάδοση με άλλους τρόπους οποιουδήποτε περιεχομένου είναι παράνομο, βλαβερό, απειλητικό, προσβλητικό, ενοχλητικό, συκοφαντικό, δυσφημιστικό, χυδαίο, άσεμνο, λιβελογραφικό, αποτελεί παραβίαση του απορρήτου κάποιου άλλου, δείχνει εμπάθεια, ή εκφράζει φυλετικές, εθνικές ή άλλες διακρίσεις, δύναται να προκαλέσει βλάβες σε ανήλικους με οποιονδήποτε τρόπο, δεν δικαιούται να μεταδοθεί σύμφωνα με την νομοθεσία ή τις συμβατικές ή διαχειριστικές σχέσεις, παραβιάζει οποιαδήποτε ευρεσιτεχνία, εμπορικό σήμα, εμπορικό μυστικό, πνευματικά δικαιώματα ή άλλα ιδιοκτησιακά δικαιώματα τρίτων, περιέχει ιούς λογισμικού ή οποιουσδήποτε άλλους κώδικες, αρχεία ή προγράμματα, που έχουν σχεδιαστεί με σκοπό την διακοπή, την πρόκληση βλάβης, την καταστροφή ή παρεμπόδιση της λειτουργίας οποιουδήποτε λογισμικού ή υλικού υπολογιστών, ηθελημένα ή αθέλητα παραβαίνει την ισχύουσα ελληνική και κοινοτική νομοθεσία και των διατάξεων αυτής.</p>

        <h2 className="text-[#C8A97E] font-medium text-base mt-8">Προσωπικά Στοιχεία Χρήστη</h2>
        <p>Ο χρήστης δηλώνει ρητά και ανεπιφύλακτα ότι δίδει τη συγκατάθεσή του να χρησιμοποιηθούν τα προσωπικά του στοιχεία (διεύθυνση κατοικίας, διεύθυνση ηλεκτρονικής αλληλογραφίας, τηλέφωνο κ.α.) που αυτός ο ίδιος γνωστοποιεί στο διαχειριστή του e-shop κατά τη διάρκεια της επίσκεψής του-πλοήγησης στον παρόντα δικτυακό τόπο (e-shop) και την ενδεχόμενη αγορά προϊόντων, με σκοπό να ενημερώνεται για σχετικές προωθητικές ενέργειες προϊόντων του e-shop, για διαφημιστικές καμπάνιες, νέα προϊόντα, εκπτωτικές τιμές κ.α.</p>
        <p>Η διαφύλαξη των Προσωπικών Δεδομένων των καταναλωτών αποτελεί απόλυτη προτεραιότητά μας. Η διαχείριση και προστασία των προσωπικών δεδομένων, διέπεται από τους όρους της παρούσας σύμβασης και τις σχετικές διατάξεις τόσο του ελληνικού, όσο και του κοινοτικού και διεθνούς δικαίου σχετικά με την προστασία του ατόμου από την επεξεργασία δεδομένων προσωπικού χαρακτήρα, καθώς και τις Αποφάσεις της Αρχής Προστασίας Προσωπικών Δεδομένων Προσωπικού Χαρακτήρα, τις οποίες σεβόμαστε και τηρούμε.</p>
      </div>
    </div>
  </div>
);


// Contact Page
const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Συμπληρώστε όλα τα πεδία"); return; }
    setSent(true);
    toast.success("Το μήνυμά σας στάλθηκε!");
  };

  return (
    <div className="pt-20 min-h-screen" data-testid="contact-page">
      <SEO title="Επικοινωνία" description="Επικοινωνήστε μαζί μας. Τηλ: 2310303656, Email: elixirlabgr@gmail.com. Παπαναστασίου 166, Θεσσαλονίκη." url="https://elixirlab.gr/contact" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-heading text-3xl md:text-4xl text-[#F4F0EB] tracking-tight mb-2">Επικοινωνία</h1>
        <p className="text-[#A39E93] mb-12">Επικοινωνήστε μαζί μας</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#C8A97E]/10 flex items-center justify-center flex-shrink-0">
                <Phone size={20} className="text-[#C8A97E]" />
              </div>
              <div>
                <h3 className="text-[#F4F0EB] font-medium mb-2">Τηλεφωνική Εξυπηρέτηση</h3>
                <p className="text-[#A39E93]">T: <a href="tel:2310303656" className="hover:text-[#C8A97E] transition-colors">2310303656</a></p>
                <p className="text-[#A39E93]">T: <a href="tel:6970967773" className="hover:text-[#C8A97E] transition-colors">6970967773</a></p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#C8A97E]/10 flex items-center justify-center flex-shrink-0">
                <Mail size={20} className="text-[#C8A97E]" />
              </div>
              <div>
                <h3 className="text-[#F4F0EB] font-medium mb-2">Email</h3>
                <p className="text-[#A39E93]"><a href="mailto:elixirlabgr@gmail.com" className="hover:text-[#C8A97E] transition-colors">elixirlabgr@gmail.com</a></p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#C8A97E]/10 flex items-center justify-center flex-shrink-0">
                <MapPin size={20} className="text-[#C8A97E]" />
              </div>
              <div>
                <h3 className="text-[#F4F0EB] font-medium mb-2">Τοποθεσία</h3>
                <p className="text-[#A39E93]">Παπαναστασίου 166,</p>
                <p className="text-[#A39E93]">54249, Θεσσαλονίκη</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#C8A97E]/10 flex items-center justify-center flex-shrink-0">
                <Clock size={20} className="text-[#C8A97E]" />
              </div>
              <div>
                <h3 className="text-[#F4F0EB] font-medium mb-2">Ώρες Λειτουργίας</h3>
                <p className="text-[#A39E93]">Δευ – Παρ : 9:00–20:00</p>
                <p className="text-[#A39E93]">Σαβ : 9:00–15:00</p>
                <p className="text-[#A39E93]">Κυρ: Κλειστά</p>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 pt-4">
              <a href="https://www.facebook.com/wodshop4u" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#141414] border border-[#F4F0EB]/10 flex items-center justify-center text-[#A39E93] hover:text-[#C8A97E] hover:border-[#C8A97E] transition-all" data-testid="social-facebook">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/wodshop4u/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#141414] border border-[#F4F0EB]/10 flex items-center justify-center text-[#A39E93] hover:text-[#C8A97E] hover:border-[#C8A97E] transition-all" data-testid="social-instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#141414] border border-[#F4F0EB]/5 p-6 md:p-8">
            {sent ? (
              <div className="text-center py-12">
                <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                <h3 className="font-heading text-2xl text-[#F4F0EB] mb-2">Ευχαριστούμε!</h3>
                <p className="text-[#A39E93]">Θα επικοινωνήσουμε μαζί σας σύντομα.</p>
                <Button onClick={() => { setSent(false); setForm({ name: '', email: '', message: '' }); }} className="btn-primary mt-6">Νέο Μήνυμα</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <h3 className="font-heading text-2xl text-[#F4F0EB] mb-4">Στείλτε μας μήνυμα</h3>
                <div className="space-y-2">
                  <Label className="text-[#A39E93]">Όνομα *</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" required data-testid="contact-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A39E93]">Email *</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB]" required data-testid="contact-email" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A39E93]">Το μήνυμά σας *</Label>
                  <Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="bg-[#0A0A0A] border-[#F4F0EB]/15 text-[#F4F0EB] min-h-[120px]" required data-testid="contact-message" />
                </div>
                <Button type="submit" className="btn-primary w-full py-3" data-testid="contact-submit">Αποστολή</Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-[#0A0A0A] border-t border-[#F4F0EB]/10 py-12">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
        <div>
          <div className="flex items-center gap-2">
            <svg width="24" height="24" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 4C20 4 12 16 12 24C12 28.4183 15.5817 32 20 32C24.4183 32 28 28.4183 28 24C28 16 20 4 20 4Z" stroke="#C8A97E" strokeWidth="1.5" fill="none"/>
            </svg>
            <span className="font-heading text-2xl text-[#C8A97E]">ELIXIR LAB</span>
          </div>
          <p className="text-[#7A756C] text-sm mt-2">Online Perfume E-Shop</p>
          <div className="flex gap-3 mt-4">
            <a href="https://www.facebook.com/wodshop4u" target="_blank" rel="noopener noreferrer" className="text-[#7A756C] hover:text-[#C8A97E] transition-colors">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href="https://www.instagram.com/wodshop4u/" target="_blank" rel="noopener noreferrer" className="text-[#7A756C] hover:text-[#C8A97E] transition-colors">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            </a>
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-[#A39E93] text-sm font-medium uppercase tracking-wider mb-3">Σύνδεσμοι</p>
          <Link to="/" className="block text-[#7A756C] hover:text-[#C8A97E] text-sm transition-colors">Αρχική</Link>
          <Link to="/shop" className="block text-[#7A756C] hover:text-[#C8A97E] text-sm transition-colors">Κατάστημα</Link>
          <Link to="/cart" className="block text-[#7A756C] hover:text-[#C8A97E] text-sm transition-colors">Καλάθι</Link>
          <Link to="/contact" className="block text-[#7A756C] hover:text-[#C8A97E] text-sm transition-colors">Επικοινωνία</Link>
          <Link to="/terms" className="block text-[#7A756C] hover:text-[#C8A97E] text-sm transition-colors">Όροι Χρήσης</Link>
        </div>
        <div className="space-y-2">
          <p className="text-[#A39E93] text-sm font-medium uppercase tracking-wider mb-3">Πληροφορίες</p>
          <p className="text-[#7A756C] text-sm">Μεταφορικά: €3.00</p>
          <p className="text-[#7A756C] text-sm">Αντικαταβολή (+€2)</p>
          <p className="text-[#7A756C] text-sm">PayPal / Τραπεζική</p>
        </div>
        <div className="space-y-2">
          <p className="text-[#A39E93] text-sm font-medium uppercase tracking-wider mb-3">Επικοινωνία</p>
          <p className="text-[#7A756C] text-sm">T: 2310303656</p>
          <p className="text-[#7A756C] text-sm">T: 6970967773</p>
          <p className="text-[#7A756C] text-sm">E: elixirlabgr@gmail.com</p>
          <p className="text-[#7A756C] text-sm">Παπαναστασίου 166, Θεσσαλονίκη</p>
        </div>
      </div>
      <div className="border-t border-[#F4F0EB]/5 pt-8 text-center">
        <p className="text-[#7A756C] text-xs">© 2026 Elixir Lab. Με επιφύλαξη παντός δικαιώματος.</p>
      </div>
    </div>
  </footer>
);

// App
function App() {
  useEffect(() => { document.title = "Elixir Lab"; }, []);
  return (
    <HelmetProvider>
    <div className="App min-h-screen bg-[#0A0A0A]">
      <BrowserRouter>
        <AuthProvider>
        <CartProvider>
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/admin" element={<AdminProtected />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
            </Routes>
          </main>
          <Footer />
          <Toaster position="top-right" richColors />
        </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
